import React, { useState, useEffect } from 'react';
import { 
  Award, 
  Users, 
  Bell, 
  Star, 
  Shield, 
  HelpCircle, 
  LayoutDashboard, 
  ChevronLeft,
  ChevronRight,
  FileText,
  Calendar,
  TrendingUp,
  CheckSquare,
  Mail,
  ClipboardList,
  Hash,
  BarChart3
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';
import { useAdmin } from '../../hooks/useAdmin';
import { useOwner } from '../../hooks/useOwner';
import { useAuth } from '../../hooks/useAuth';
import type { AppView } from '../../types/views';
import { supabaseReminderStorage, supabaseReviewStorage, supabaseAdminTasksStorage } from '../../services/supabaseStorage';
import { FeatureVisibilityService } from '../../services/featureVisibilityService';
import { countUrgentProcessNumberSessions } from '../../services/processNumbersService';
import { SystemSettingsService } from '../../services/systemSettingsService';
import { UserProfileSection } from './UserProfileSection';
import { useToast } from '../../contexts/ToastContext';

interface ModernSidebarProps {
  activeView: AppView;
  onViewChange: (view: AppView, context?: any) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  view: AppView;
  badge?: number;
  urgent?: boolean;
  group: 'core' | 'admin' | 'help';
  description?: string;
  requiresSuperAdmin?: boolean;
  requiresAdmin?: boolean;
  requiresOwner?: boolean;
  hideForSuperAdmin?: boolean;
}

const navigationGroups = {
  core: {
    label: '',
    description: ''
  },
  admin: {
    label: 'Administration',
    description: 'System administration'
  },
  help: {
    label: 'Support',
    description: 'Help and documentation'
  }
};

export function ModernSidebar({ activeView, onViewChange, collapsed, onToggleCollapse }: ModernSidebarProps) {
  const [expiringCount, setExpiringCount] = useState(0);
  const [reviewsDueCount, setReviewsDueCount] = useState(0);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [processNumbersAlertCount, setProcessNumbersAlertCount] = useState(0);
  const [featureVisibility, setFeatureVisibility] = useState<Map<string, boolean>>(new Map());
  const [companyName, setCompanyName] = useState('Rapid Cert');
  const [companyLogoUrl, setCompanyLogoUrl] = useState('/images/logo.png');
  const { isSuperAdmin } = useSuperAdmin();
  const { isAdmin } = useAdmin();
  const { isOwner } = useOwner();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { showSuccess } = useToast();

  const handleSignOut = async () => {
    await signOut();
    navigate('/thank-you');
  };

  const handleBackupData = () => {
    showSuccess('Backup download started.');
  };

  // Load notification counts
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [allReminders, allReviewReminders, pendingTasksCount, urgentProcessSessions] = await Promise.all([
          supabaseReminderStorage.getReminders(),
          supabaseReviewStorage.getReviewReminders(),
          supabaseAdminTasksStorage.getPendingTasksCount(),
          countUrgentProcessNumberSessions(),
        ]);
        
        // Count unrenewed reminders (active certificates that need tracking)
        // Only count certificates expiring within 30 days, not all unrenewed certificates
        const today = new Date().toISOString().split('T')[0];
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
        
        const activeRemindersCount = allReminders.filter(r => {
          if (r.isRenewed) return false;
          if (r.expiryDate === 'Invalid Date') return false;
          
          let expiryDate: Date;
          
          // Try parsing as ISO format first (YYYY-MM-DD)
          if (r.expiryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
            expiryDate = new Date(r.expiryDate);
            if (isNaN(expiryDate.getTime())) return false;
          } else {
            // Parse DD-MM-YYYY format (legacy)
            const parts = r.expiryDate.split('-');
            if (parts.length !== 3) return false;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            const year = parseInt(parts[2], 10);
            expiryDate = new Date(year, month, day);
            if (isNaN(expiryDate.getTime())) return false;
          }
          
          // Use consistent date comparison - only show certificates expiring within 30 days (not expired)
          const todayDate = new Date(today);
          return expiryDate >= todayDate && expiryDate <= thirtyDaysFromNow;
        }).length;
        
        setExpiringCount(activeRemindersCount);
        
        // Count review reminders that are not completed
        const activeReviewsCount = allReviewReminders.filter(r => !r.isCompleted).length;
        setReviewsDueCount(activeReviewsCount);
        
        // Set pending tasks count
        setPendingTasksCount(pendingTasksCount);
        setProcessNumbersAlertCount(urgentProcessSessions);
      } catch (error) {
        console.error('Error loading notification counts:', error);
      }
    };

    loadCounts();
    const interval = setInterval(loadCounts, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Load feature visibility settings
  useEffect(() => {
    const loadFeatureVisibility = async () => {
      try {
        const settings = await FeatureVisibilityService.getAllFeatureSettings();
        const visibilityMap = new Map<string, boolean>();
        settings.forEach(setting => {
          visibilityMap.set(setting.feature_key, setting.is_enabled);
        });
        setFeatureVisibility(visibilityMap);
      } catch (error) {
        console.error('Error loading feature visibility:', error);
        // Default to all enabled if there's an error (fail-open approach)
        // Don't set anything, let it default to true in the render function
      }
    };

    loadFeatureVisibility();
  }, []);

  // Load company branding
  useEffect(() => {
    const loadCompanyBranding = async () => {
      try {
        const branding = await SystemSettingsService.getCompanyBranding();
        if (branding.company_name) {
          setCompanyName(branding.company_name);
        }
        if (branding.company_logo_url) {
          setCompanyLogoUrl(branding.company_logo_url);
        }
      } catch (error) {
        console.error('Error loading company branding:', error);
        // Use defaults on error
      }
    };

    loadCompanyBranding();
  }, []);

  const navigationItems: NavigationItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      view: 'dashboard',
      group: 'core',
      description: 'Overview and analytics'
    },
    {
      id: 'certificates',
      label: 'Certificates',
      icon: Award,
      view: 'certificates',
      group: 'core',
      description: 'Generate and manage certificates'
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: Users,
      view: 'clients',
      group: 'core',
      description: 'Customer relationship management'
    },
    {
      id: 'reminders',
      label: 'Reminders',
      icon: Bell,
      view: 'reminders',
      group: 'core',
      description: 'Certificate expiry tracking',
      badge: expiringCount
    },
    // Reviews and Ops Day Planner are hidden from the main sidebar per
    // 2026-05-10 product feedback. The routes/components remain so deep
    // links (e.g. /?view=reviews) and dashboard quick-links keep working.
    {
      id: 'bookings',
      label: 'Bookings',
      icon: Calendar,
      view: 'bookings',
      group: 'core',
      description: 'Schedule and manage training'
    },
    {
      id: 'process-numbers',
      label: 'Process Numbers / Safe Pass Cards',
      icon: Hash,
      view: 'process-numbers',
      group: 'core',
      description: 'Safe Pass process numbers and card tracking',
      badge: processNumbersAlertCount > 0 ? processNumbersAlertCount : undefined,
      urgent: processNumbersAlertCount > 0,
    },
    {
      id: 'quotes',
      label: 'Quotes',
      icon: FileText,
      view: 'quotes',
      group: 'core',
      description: 'Create and manage quotes'
    },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: CheckSquare,
      view: 'tasks',
      group: 'core',
      description: 'Manage tasks and customer reminders',
      badge: pendingTasksCount
    },
    {
      id: 'emails',
      label: 'Comms',
      icon: Mail,
      view: 'emails',
      group: 'core',
      description: 'Comms and engagement dashboard'
    },
    {
      id: 'financial',
      label: 'Financial Reports',
      icon: TrendingUp,
      view: 'financial',
      group: 'admin',
      description: 'Revenue tracking and profit analysis',
      requiresAdmin: true
    },
    {
      id: 'owner-analytics',
      label: 'Efficiency Dashboard',
      icon: BarChart3,
      view: 'owner-analytics',
      group: 'admin',
      description: 'Admin throughput, SLA, and quality metrics',
      requiresOwner: true
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: Shield,
      view: 'admin',
      group: 'admin',
      description: 'Administration panel',
      requiresAdmin: true,
      hideForSuperAdmin: true
    },
    {
      id: 'superadmin',
      label: 'Super Admin',
      icon: Shield,
      view: 'superadmin',
      group: 'admin',
      description: 'System administration',
      requiresSuperAdmin: true
    },
    {
      id: 'faq',
      label: 'Help & FAQ',
      icon: HelpCircle,
      view: 'faq',
      group: 'help',
      description: 'Frequently asked questions'
    }
  ];

  // Filter items based on permissions
  const visibleItems = navigationItems.filter(item => {
    if (item.requiresSuperAdmin && !isSuperAdmin) return false;
    if (item.requiresOwner && !isOwner && !isSuperAdmin) return false;
    if (item.requiresAdmin && !isAdmin && !isSuperAdmin) return false;
    if (item.hideForSuperAdmin && isSuperAdmin) return false;
    return true;
  });

  // Group items by category
  const groupedItems = visibleItems.reduce((groups, item) => {
    if (!groups[item.group]) {
      groups[item.group] = [];
    }
    groups[item.group].push(item);
    return groups;
  }, {} as Record<string, NavigationItem[]>);

  const renderNavigationItem = (item: NavigationItem) => {
    const isActive = activeView === item.view;
    const Icon = item.icon;
    
    // Check if feature is enabled (default to true if not loaded yet or not found)
    // Super Admin is always enabled for super admins
    const isFeatureEnabled = item.id === 'superadmin' && isSuperAdmin
      ? true
      : (featureVisibility.get(item.id) ?? true);
    
    const isDisabled = !isFeatureEnabled;

    const handleClick = () => {
      if (!isDisabled) {
        onViewChange(item.view);
      }
    };

    return (
      <button
        key={item.id}
        onClick={handleClick}
        disabled={isDisabled}
        className={`group relative w-full flex items-center px-4 py-3 text-left rounded-xl transition-all duration-300 ${
          isDisabled
            ? 'opacity-50 cursor-not-allowed text-slate-400'
            : isActive
            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25'
            : item.urgent
            ? 'border border-red-200 bg-red-50 text-red-900 hover:bg-red-100/80 hover:shadow-md'
            : 'text-slate-700 hover:bg-white/80 hover:text-slate-900 hover:shadow-md'
        }`}
        title={
          collapsed 
            ? item.label 
            : isDisabled 
            ? `${item.label} (Disabled)` 
            : item.label
        }
      >
        {/* Icon */}
        <div className={`flex-shrink-0 transition-transform duration-300 ${
          isDisabled ? '' : isActive ? 'scale-110' : 'group-hover:scale-105'
        }`}>
          <Icon className={`w-5 h-5 ${collapsed ? 'mx-auto' : 'mr-3'}`} />
        </div>

        {/* Label and Description */}
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="font-medium truncate">{item.label}</span>
              {item.badge && item.badge > 0 && (
                <span className={`ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                  isActive 
                    ? 'bg-white/20 text-white' 
                    : 'bg-red-500 text-white'
                }`}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </div>
            {item.description && (
              <p className={`text-xs mt-1 truncate ${
                isDisabled 
                  ? 'text-slate-400' 
                  : isActive 
                  ? 'text-white/70' 
                  : 'text-slate-500'
              }`}>
                {item.description}
              </p>
            )}
          </div>
        )}

        {/* Collapsed Badge */}
        {collapsed && item.badge && item.badge > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}

        {/* Active Indicator */}
        {isActive && (
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-white rounded-r-full"></div>
        )}
      </button>
    );
  };

  return (
    <div className={`fixed left-0 top-0 h-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-r border-slate-200/50 dark:border-slate-700/50 shadow-2xl transition-all duration-300 ease-in-out z-30 flex flex-col ${
      collapsed ? 'w-20' : 'w-80'
    }`}>
      {/* Logo Section */}
      <div className="flex items-center justify-between p-6 border-b border-slate-200/50 dark:border-slate-700/50 flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl shadow-lg overflow-hidden bg-white dark:bg-slate-800">
              {companyLogoUrl ? (
                <img 
                  src={companyLogoUrl} 
                  alt={`${companyName} Logo`}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    // Fallback to default logo if configured logo fails to load
                    (e.target as HTMLImageElement).src = '/images/logo.png';
                  }}
                />
              ) : (
                <img 
                  src="/images/logo.png" 
                  alt="Logo" 
                  className="w-full h-full object-contain"
                />
              )}
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-white dark:to-slate-200 bg-clip-text text-transparent">
                {companyName}
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Certificate Management</p>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                Powered by <a href="https://rapidcert.eu" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 dark:hover:text-slate-300 underline">RapidCert</a>
              </p>
            </div>
          </div>
        )}
        
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          ) : (
            <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          )}
        </button>
      </div>

      {/* Navigation Groups */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-6 px-4 space-y-8 min-h-0">
        {Object.entries(groupedItems).map(([groupKey, items]) => {
          const group = navigationGroups[groupKey as keyof typeof navigationGroups];
          
          return (
            <div key={groupKey}>
              {!collapsed && groupKey !== 'core' && (
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 mb-2">
                    {group.label}
                  </h3>
                  {group.description && (
                    <p className="text-xs text-slate-400 dark:text-slate-500 px-4 mb-3">
                      {group.description}
                    </p>
                  )}
                </div>
              )}
              
              <div className="space-y-1">
                {items.map(renderNavigationItem)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-200/50 dark:border-slate-700/50 p-3 flex-shrink-0">
        <UserProfileSection
          collapsed={collapsed}
          onBackupData={handleBackupData}
          onSignOut={handleSignOut}
        />
      </div>

      {/* Collapse Handle for Desktop */}
      <button
        onClick={onToggleCollapse}
        className="absolute -right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
      >
        {collapsed ? (
          <ChevronRight className="w-3 h-3 text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200" />
        ) : (
          <ChevronLeft className="w-3 h-3 text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200" />
        )}
      </button>
    </div>
  );
}